import React, {useMemo} from "react";
import {Typography} from "@mui/material";
import {useDeskStore} from "../../../api/DeskStore";
import {DateRangeOptions} from "../../../components/filter-fields/DateRangeFilter";
import DeskChip from "../../../components/desk-chip/DeskChip";
import {RemoteValue} from "../../../util/RemoteValue";
import {AppPages, usePageUrl} from "../../../app-layout/AppPages";
import {
    Desks_Read,
    Employees_Read,
    KeyData_Read,
    NewJoiners_Read,
    VisitReports_Read,
} from "../../../util/access/Permission";
import {CsvModel} from "../../../util/CSVWriteUtil";
import {DashboardWidget, DashboardWidgetItem} from "../DashboardWidget";
import {
    DeskAssignedToLowAttendanceEmployee,
    findDesksAssignedToLowAttendanceEmployees,
} from "./DesksAssignedToLowAttendanceEmployees";
import NeighbourhoodColorIcon from "../../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import {WidgetEmployeeVisitFigure} from "../WidgetFigure";
import {useDeskModelStore} from "../../../api/DeskModelStore";

const dateRange = DateRangeOptions.lastFourMonths;

const csvModel = RemoteValue.fromValue(new CsvModel<DeskAssignedToLowAttendanceEmployee>([
    {header: 'Desk', value: ({desk}) => desk.name},
    {header: 'Room', value: ({desk}) => desk.roomName},
    {header: 'Person', value: ({employee}) => employee.fullName},
    {header: 'Workday visits', value: ({visits}) => visits.workDayVisits.toString()},
    {header: 'Eligible workdays', value: ({visits}) => visits.tenureDayCount.toString()},
    {header: 'Visit rate', value: ({visits}) => `${visits.percent}%`},
]));

export function DesksAssignedToLowAttendanceEmployeesWidget() {
    const pageUrl = usePageUrl();
    const desks = useDeskStore(state => state.remoteValue);
    const deskModel = useDeskModelStore(dateRange);
    const problems = useMemo(() => RemoteValue
            .combine({deskModel, desks})
            .map(({deskModel, desks}) => {
                return findDesksAssignedToLowAttendanceEmployees(deskModel, desks);
            }),
        [deskModel, desks],
    );

    return (
        <DashboardWidget
            title="Desks assigned to employees with low attendance"
            description="Assigned desks whose employees visited on less than 30% of eligible workdays in the last four months."
            problems={problems}
            requiredPermissions={[Desks_Read, Employees_Read, KeyData_Read, NewJoiners_Read, VisitReports_Read]}
            link={pageUrl(AppPages.desks, {}, {
                problem: 'low-attendance',
                dateRange: DateRangeOptions.encode(dateRange),
            })}
            csvModel={csvModel}
            renderProblem={({desk, employee, neighbourhood, visits}, index) => (
                <DashboardWidgetItem
                    key={desk.id}
                    index={index}
                    left={(
                        <>
                            <DeskChip desk={desk} deskAssignee={employee} size="small"/>
                            {neighbourhood && (
                                <>
                                    <NeighbourhoodColorIcon neighbourhood={neighbourhood} size={24}/>
                                    <Typography variant="body2">{neighbourhood.name}</Typography>
                                </>
                            )}
                        </>
                    )}
                    right={(
                        <WidgetEmployeeVisitFigure employeeVisits={visits}/>
                    )}
                />
            )}
        />
    );
}
